import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMount, useUnmount } from "react-use";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Alert, AlertTitle, Box, Button, Paper, Typography } from "@mui/material";
import { Feature, LineString, Point } from "geojson";
import rbush from "geojson-rbush";
import { omit } from "lodash";
import { FeatureIdentifier } from "mapbox-gl";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api";
import { DRAWING_CONSTANTS, DRAWING_PROPERTIES_TO_EXCLUDE } from "fond/constants";
import DrawHandler from "fond/draw/DrawHandler";
import directSelect from "fond/draw/modes/design/underground/directSelect";
import drawLineString from "fond/draw/modes/design/underground/drawLineString";
import drawPoint from "fond/draw/modes/design/underground/drawPoint";
import { isValid } from "fond/draw/modes/design/underground/helper";
import simpleSelectUG from "fond/draw/modes/design/underground/simpleSelectUG";
import { createStyles } from "fond/draw/modes/design/underground/ugDrawStyles";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { LineStringButton, PointButton, PolygonSelectButton, SelectButton, ToolbarConfig } from "fond/map/Toolbar";
import { EditMode, setEditMode } from "fond/project/redux";
import { addImport, removeImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { ImportStatus } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import SkeletonLoadingGrid from "fond/widgets/SkeletonLoadingGrid";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import { LayerIds, sourceId } from "../helper";

const ugDrawStyles = createStyles({ pathLayerId: LayerIds.ugPaths, pointLayerId: LayerIds.ugPoints });

interface UGDrawPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const config: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="polygonSelect" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />, <LineStringButton key="drawLineString" />],
  },
];

const modes = {
  simple_select: simpleSelectUG,
  direct_select: directSelect,
  draw_point: drawPoint,
  draw_line_string: drawLineString,
};

const sources: FeatureIdentifier[] = [
  { source: sourceId, sourceLayer: LayerIds.ugPaths },
  { source: sourceId, sourceLayer: LayerIds.ugPoints },
];

const UGDrawPanel: React.FC<UGDrawPanelProps> = ({ isReadOnly }) => {
  const dispatch = useAppDispatch();
  const { drawControl, setDrawMode } = useContext(MapContext);
  const { goBack } = useStackedNavigationContext();
  const { enqueueSnackbar } = useSnackbar();
  const versionId = useAppSelector((state) => state.project.versionId);
  const ugPathsLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: LayerIds.ugPaths }));
  const ugPointsLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: LayerIds.ugPoints }));
  const { data: ugPathsData, isLoading: isLoadingUgPaths } = useGetFeaturesQuery({ layerId: ugPathsLayer?.ID || "" }, { skip: !ugPathsLayer?.ID });
  const { data: ugPointsData, isLoading: isLoadingUgPoints } = useGetFeaturesQuery(
    { layerId: ugPointsLayer?.ID || "" },
    { skip: !ugPointsLayer?.ID }
  );
  const [isSaving, setIsSaving] = useState(false);
  const isLoading = isLoadingUgPaths || isLoadingUgPoints;
  // Watch the import state for these layers
  const importStatePaths = useAppSelector((state) => state.imports[versionId]?.[LayerIds.ugPaths]);
  const importStatePoints = useAppSelector((state) => state.imports[versionId]?.[LayerIds.ugPoints]);

  // The initial features are derived from the data layer for lead-ins which
  // are drawn as LineStrings.
  const initialFeatures: Feature<LineString>[] = useMemo(() => {
    if (!isLoading) {
      // Clone and add layerId to each feature type
      const clonedUgPoints = (structuredClone(ugPointsData) || []) as Feature<Point>[];
      const clonedUgPaths = (structuredClone(ugPathsData) || []) as Feature<LineString>[];

      clonedUgPoints.forEach((f) => {
        f.properties = f.properties || {};
        f.properties.layerId = LayerIds.ugPoints;
      });

      clonedUgPaths.forEach((f) => {
        f.properties = f.properties || {};
        f.properties.layerId = LayerIds.ugPaths;
      });

      // Build spatial index for efficient validation
      const tree = rbush();
      tree.load([...clonedUgPaths, ...clonedUgPoints]);

      return tree.all().features.map((f) => {
        if (f.properties) {
          f.properties[`${DRAWING_CONSTANTS.Valid}`] = String(
            isValid(f, tree, {
              pathLayerId: LayerIds.ugPaths,
              pointLayerId: LayerIds.ugPoints,
            })
          );
        }
        return f;
      });
    }

    return [];
  }, [ugPathsData, isLoading, ugPointsData]);

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  useUnmount(() => {
    dispatch(setEditMode(EditMode.none));
  });

  const handleBack = useCallback(() => {
    setDrawMode("no_feature");
    goBack();
  }, [setDrawMode, goBack]);

  // Monitor import completion and navigate away when done
  useEffect(() => {
    if (!importStatePaths || !importStatePoints || !isSaving) return;

    const isComplete = importStatePaths.status === ImportStatus.COMPLETE && importStatePoints.status === ImportStatus.COMPLETE;
    const isError = importStatePaths.status === ImportStatus.ERROR || importStatePoints.status === ImportStatus.ERROR;

    if (isComplete) {
      // Remove the import from state to close the progress popup
      dispatch(removeImport({ versionId, layerKey: LayerIds.ugPaths }));
      dispatch(removeImport({ versionId, layerKey: LayerIds.ugPoints }));
      // Import finished successfully, navigate away
      handleBack();
    } else if (isError) {
      enqueueSnackbar("Failed to save changes. Please try again.", { variant: "error" });
      // Remove the import from state so user can retry
      dispatch(removeImport({ versionId, layerKey: LayerIds.ugPaths }));
      dispatch(removeImport({ versionId, layerKey: LayerIds.ugPoints }));
      // Stop loading state
      setIsSaving(false);
    }
  }, [handleBack, importStatePaths, importStatePoints, isSaving, dispatch, versionId, enqueueSnackbar]);

  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        allLayerIds: [LayerIds.ugPoints, LayerIds.ugPaths],
        editingLayerIds: [LayerIds.ugPaths],
        snap: true,
        snapOptions: {
          snapPx: 25,
          snapToMidPoints: false,
          snapVertexPriorityDistance: 0.0025,
        },
        modeOptions: {
          pathLayerId: LayerIds.ugPaths,
          pointLayerId: LayerIds.ugPoints,
        },
      }) as MapboxDrawOptions,
    []
  );

  const handleOnConfirm = async () => {
    setIsSaving(true);

    const allFeatures = drawControl.current?.getAll().features || [];

    // Split features by layer
    const pathFeatures = allFeatures
      .filter((f) => f.properties?.layerId === LayerIds.ugPaths)
      .map((f) => ({
        ...f,
        properties: omit(f.properties, DRAWING_PROPERTIES_TO_EXCLUDE),
      }));

    const pointFeatures = allFeatures
      .filter((f) => f.properties?.layerId === LayerIds.ugPoints)
      .map((f) => ({
        ...f,
        properties: omit(f.properties, DRAWING_PROPERTIES_TO_EXCLUDE),
      }));

    // Create feature collections
    const pathsFC = featureCollection(pathFeatures);
    const pointsFC = featureCollection(pointFeatures);

    // Create files
    const pathsFile = new File([JSON.stringify(pathsFC)], "ug_paths.json", {
      type: "text/plain",
    });

    const pointsFile = new File([JSON.stringify(pointsFC)], "ug_points.json", {
      type: "text/plain",
    });

    // Dispatch imports for both layers
    dispatch(
      addImport({
        versionId,
        layerKey: LayerIds.ugPaths,
        displayName: "UG Paths",
        files: [pathsFile],
      })
    );

    dispatch(
      addImport({
        versionId,
        layerKey: LayerIds.ugPoints,
        displayName: "UG Points",
        files: [pointsFile],
      })
    );
  };

  /**
   * Determines the initial properties for any new feature being drawn.
   */
  const getFeatureProperties = useCallback(
    (feature: Feature<LineString | Point>) => {
      const tree = rbush();
      tree.load(drawControl.current?.getAll().features || []);
      if (feature.geometry.type === "Point") {
        return {
          layerId: LayerIds.ugPoints,
          [DRAWING_CONSTANTS.Valid]: tree && isValid(feature, tree, { pointLayerId: LayerIds.ugPoints }).toString(),
        };
      }
      return { layerId: LayerIds.ugPaths, [DRAWING_CONSTANTS.Valid]: tree && isValid(feature, tree, { pathLayerId: LayerIds.ugPaths }).toString() };
    },
    [drawControl]
  );

  if (isLoadingUgPaths || isLoadingUgPoints) {
    return <SkeletonLoadingGrid spacing={2} items={[{ height: 40 }, { height: 130 }, { height: 38 }, { height: 300 }]} />;
  }

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Draw on map" />

      {isSaving ? (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Saving changes" subtext="Processing..." />
        </Paper>
      ) : (
        <Alert severity="info">
          <AlertTitle>Edit Underground</AlertTitle>
        </Alert>
      )}

      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleBack}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          size="small"
          onClick={handleOnConfirm}
          sx={{ ml: 1, px: 2 }}
          data-testid="finish-button"
          disabled={isReadOnly} // TODO - isDirty?
          loading={isSaving}
        >
          Finish
        </LoadingButton>
      </Box>

      <DrawHandler
        startingMode="simple_select"
        initialFeatures={initialFeatures}
        sources={sources}
        config={config}
        options={options}
        styles={ugDrawStyles}
        modes={modes}
        getFeatureProperties={getFeatureProperties}
      />

      {isSaving && (
        <MapOverlay>
          <Paper sx={{ py: 1, px: 3, textAlign: "center", width: 200 }}>
            <BlockSpinner spinnerProps={{ size: 20 }} />
            <Typography component="p" variant="caption" pt={1}>
              Saving changes...
            </Typography>
          </Paper>
        </MapOverlay>
      )}
    </>
  );
};

export default UGDrawPanel;
